import { Component, OnInit, DestroyRef, effect, inject, untracked, signal, input, computed } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Integration } from '../../models/integration';
import { ClientInformation } from 'src/app/models/client-information';
import { IntegrationError } from '../../models/integration-error';
import { ClientIntegrationService } from '../../services/client-integration.service';
import { IntegrationErrorsService } from '../../services/integration-errors.service';
import { ToastService } from '../../services/toast.service';
import { Server } from 'src/app/models/server';
import { ServerService } from 'src/app/services/server.service';
import { TypeConstantService } from 'src/app/services/type-constant.service';
import { DestinationEndpoint } from '../../models/destination-endpoint';
import { ServiceError } from 'src/app/models/service-error';
import { ErrorCategory } from 'src/app/models/error-category';
import { CurrentStatus } from 'src/app/models/currentStatus';
import { ImportType } from 'src/app/models/import-type';
import { ErrorSearchFilter } from 'src/app/models/error-search-filter';
import { GridSort } from 'src/app/features/grid/classes/grid-config.types';
import { passUntilDestroyed } from 'src/app/utilities/rx-operators';

@Component({
  selector: 'app-error-messages-v3',
  templateUrl: './error-messages-v3.html',
  styleUrls: ['./error-messages-v3.scss'],
  providers: [ClientIntegrationService, IntegrationErrorsService],
  standalone: false,
})
export class ErrorMessagesV3Component implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  integration = input<Integration>(null);
  isInTab = input<boolean>(false);
  currentClient = input<ClientInformation>(null);

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly errorLoggingService = inject(IntegrationErrorsService);
  private readonly toastService = inject(ToastService);
  private readonly typeService = inject(TypeConstantService);
  private readonly clientService = inject(ClientIntegrationService);
  private readonly serverService = inject(ServerService);

  tabIndex = 0;

  dataLoading = signal(true);
  allPopulated = signal(false);
  serviceErrorsPopulated = signal(false);
  criticalPopulated = signal(false);

  allErrorsList = signal<IntegrationError[]>([]);
  serviceErrorList = signal<ServiceError[]>([]);
  criticalErrorsList = signal<IntegrationError[]>([]);

  public allDestinations = signal<DestinationEndpoint[]>([]);
  public categories = signal<ErrorCategory[]>([]);
  public statuses = signal<CurrentStatus[]>([]);
  public importTypes = signal<ImportType[]>([]);

  integrations = signal<Integration[]>([]);
  clients = signal<ClientInformation[]>([]);
  servers = signal<Server[]>([]);
  integrationsMap = computed(() => {
    const list = this.integrations();
    return list.length > 0
      ? new Map(list.map(k => [k.integrationId, k] as [string, Integration]))
      : null;
  });

  critErrorSearchFilter = signal(new ErrorSearchFilter(null, null, 45));
  allErrorSearchFilter = signal(new ErrorSearchFilter(null, null, 30));

  critErrorSorts: GridSort[] = [{ field: 'statusName', order: -1 }, { field: 'receivedDate', order: -1 }];
  allErrorSorts: GridSort[] = [{ field: 'receivedDate', order: -1 }];

  gettingClients = signal(false);

  constructor() {
    effect(() => {
      const integration = this.integration();
      if (integration) {
        untracked(() => {
          this.resetAll();
          this.loadForIntegration();
        });
      }
    });

    let allFilterFirstRun = true;
    effect(() => {
      const _ = this.allErrorSearchFilter();
      if (allFilterFirstRun) { allFilterFirstRun = false; return; }
      untracked(() => {
        if (this.integration()) return;
        this.allPopulated.set(false);
        this.getAllErrors();
      });
    });

    let critFilterFirstRun = true;
    effect(() => {
      const _ = this.critErrorSearchFilter();
      if (critFilterFirstRun) { critFilterFirstRun = false; return; }
      untracked(() => {
        this.criticalPopulated.set(false);
        this.getCriticalErrors();
      });
    });
  }

  //#region init

  ngOnInit() {
    this.getTypesAndDestinations();

    if (this.integration() == null || this.integration() == undefined || this.integration().integrationId == '') {
      this.resetAll();
      this.getClients();
      this.getIntegrations();
      this.loadAllErrors();

      if (!this.isInTab()) {
        this.checkAllServersLastDataSent();
      }
    }
  }

  private loadForIntegration(): void {
    this.integrations.set([this.integration()]);
    if (this.currentClient() != null && this.currentClient() != undefined) {
      this.clients.set([this.currentClient()]);
    }
    this.loadAllErrors();

    if (!this.isInTab()) {
      this.checkAllServersLastDataSent();
    }
  }

  //#endregion

  resetAll(): void {
    this.tabIndex = 0;

    this.dataLoading.set(true);
    this.allPopulated.set(false);
    this.serviceErrorsPopulated.set(false);
    this.criticalPopulated.set(false);
    this.allErrorsList.set([]);
    this.serviceErrorList.set([]);
    this.criticalErrorsList.set([]);
    this.integrations.set([]);

    this.gettingClients.set(false);
    this.clients.set([]);

    this.servers.set([]);
    this.allDestinations.set([]);
    this.categories.set([]);
    this.statuses.set([]);
    this.importTypes.set([]);
  }

  private loadAllErrors(): void {
    this.dataLoading.set(true);
    if (this.isInTab()) {
      this.getAllErrors();
    } else {
      this.getCriticalErrors();
      this.getAllErrors();
      this.getServiceErrors();
    }
  }

  onTabSelect(event: any) {
    this.tabIndex = event.index;
  }

  getIntegrations(): void {
    this.clientService.getAllIntegrations()
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe(
        (g) => {
          this.integrations.set(g);
        });
  }

  getClients(): void {
    this.gettingClients.set(true);
    if (this.isInTab()) {
      this.clients.set([this.currentClient()]);
      this.gettingClients.set(false);
    }
    else {
      this.clientService.getAllClients()
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe((clients) => {
        this.clients.set(clients);
        this.gettingClients.set(false);
      },
      (_error) =>{
        this.gettingClients.set(false);
      });
    }
  }

  getTypesAndDestinations(): void {
    this.typeService.getAllDestinationEndpoints()
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe(r => {
          this.allDestinations.set(r);
        });

    this.typeService.getAllImportTypes()
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe(r => {
        this.importTypes.set(r);
      });

    this.errorLoggingService.getAllErrorCategories()
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe(r => {
          this.categories.set(r);
        });

    this.errorLoggingService.getAllCurrentStatuses()
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe((r) => {
          this.statuses.set(r);
      });
  }

  getAllErrors(): void {
    let eObs = null;
    if (this.integration() == null){
      eObs = this.errorLoggingService.getAllErrors(this.allErrorSearchFilter());
    }
    else {
      eObs = this.errorLoggingService.getAllErrorsById(this.integration().integrationId);
    }

    if (eObs != null) {
      eObs.pipe(passUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.allErrorsList.set(data);
          this.dataLoading.set(false);
          this.allPopulated.set(true);
        },
        error: (_error) => {
          this.dataLoading.set(false);
          this.allPopulated.set(true);
          this.toastService.toastCreate('Loading Integration Errors Failed: ' + _error, 'Warning', { autoClose: true, keepAfterRouteChange: false });
        }},
      );
    }
  }

  getCriticalErrors(): void {
    this.errorLoggingService.getSnapshot(this.critErrorSearchFilter())
    .pipe(passUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (g) => {
        this.criticalErrorsList.set(g);
        this.dataLoading.set(false);
        this.criticalPopulated.set(true);
      },
      error: (_error) => {
        this.dataLoading.set(false);
        this.criticalPopulated.set(true);
        this.toastService.toastCreate('Loading Critical Integration Errors Failed: ' + _error, 'Warning', { autoClose: true, keepAfterRouteChange: false });
      },
    });
  }

  getServiceErrors(): void {
    let seObs = null;

    if (this.integration() == null) {
      seObs = this.errorLoggingService.getAllServiceErrors();
    }
    else {
      seObs = this.errorLoggingService.getAllServiceErrorsByServerId(this.integration().serverId);
    }

    if (seObs != null) {
      seObs.pipe(passUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.serviceErrorList.set(data);
          this.dataLoading.set(false);
          this.serviceErrorsPopulated.set(true);
        },
        error: (_error) => {
          this.dataLoading.set(false);
          this.serviceErrorsPopulated.set(true);
          this.toastService.toastCreate('Loading Service Errors Failed: ' + _error, 'Warning', { autoClose: true, keepAfterRouteChange: false });
        }});
    }
  }

  public checkAllServersLastDataSent() {
    this.serverService.getAllServersInformation()
    .pipe(passUntilDestroyed(this.destroyRef))
    .subscribe(
      (s) => {
        s.forEach(server => {
          if (server.serverId !== '0' && server.testServer == false) {
            if (server.lastDataSentServer !== null) {
              const lds = new Date(server.lastDataSentServer);

              if (lds !== null) {
                if (lds.getTime() < Date.now() - (server.serverAlertTimespan * 60 * 1000)) {
                  this.toastService.toastCreate('Last Data Sent Error. Server ' + server.serverId + ': ' + lds.toLocaleString(), 'Warning', { autoClose: true, keepAfterRouteChange: false, ignoreInDevEnvironment: true });
                }
              }
            }
            else if (server.integrationCount > 0) {
              this.toastService.toastCreate('Last Data Sent not populated for Server ' + server.serverId, 'Warning', { autoClose: true, keepAfterRouteChange: false, ignoreInDevEnvironment: true });
            }
          }
        });
        this.servers.set(s);
      },
      (_error) => {
        this.dataLoading.set(false);
      },
    );
  }

  public handleUpdatedIntegrationErrorFromGrid(updatedErrors: IntegrationError[], grid = 'all') {
    if (updatedErrors === null || updatedErrors.length === 0) {
      return;
    }

    updatedErrors.forEach(updatedError => {
      const allIndex = this.allErrorsList().findIndex(x => x.integrationErrorId === updatedError.integrationErrorId);
      if (allIndex !== -1) {
        this.allErrorsList.update(list => list.map((e, i) => i === allIndex ? updatedError : e));
      }

      const critIndex = this.criticalErrorsList().findIndex(x => x.integrationErrorId === updatedError.integrationErrorId);
      if (critIndex !== -1) {
        this.criticalErrorsList.update(list => list.map((e, i) => i === critIndex ? updatedError : e));
      }
    });
  }

  public handleUpdatedServiceErrorFromGrid(updatedError: ServiceError) {
    if (updatedError === null) {
      return;
    }

    const allIndex = this.serviceErrorList().findIndex(x => x.serviceErrorId === updatedError.serviceErrorId);
    if (allIndex !== -1) {
      this.serviceErrorList.update(list => list.map((e, i) => i === allIndex ? updatedError : e));
    }
  }
}
